/**
 * SyncSessionModal.jsx — Per-session sync history drill-down modal.
 *
 * Mounted once at the app root (see main.jsx). Listens for the
 * `v2-sync-session-pick` CustomEvent (detail.sessionId) — any widget
 * that wants to surface a sync session detail just dispatches that
 * event on `window`.
 *
 * Pulls from the `sync-history` widget endpoint (sessionId param).
 *
 * Issue: #240
 */

import { createSignal, createEffect, Show, For, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import api from '../lib/apiClient.js';
import { gold, qty, relTime } from '../lib/format.js';
import { pickUser } from '../lib/picks.js';
import './v2-modal.css';

function fmtDate(ms) {
  if (!ms) return '—';
  try { return new Date(ms).toLocaleString(); } catch { return String(ms); }
}
function fmtDuration(start, end) {
  if (!start || !end || end < start) return '—';
  const sec = Math.round((end - start) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}

export default function SyncSessionModal() {
  const [sessionId, setSessionId] = createSignal(null);
  const [data, setData] = createSignal(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal(null);

  function open(id) {
    if (!id) return;
    setSessionId(id);
    setData(null);
    setError(null);
  }
  function close() {
    setSessionId(null);
    setData(null);
    setError(null);
  }

  createEffect(() => {
    const id = sessionId();
    if (!id) return;
    setLoading(true);
    api.fetchWidget('sync-history', { sessionId: id })
      .then((r) => {
        const p = r?.payload || null;
        if (p?.error === 'forbidden') setError('You do not have access to this sync session.');
        else if (p?.error === 'not_found') setError('Sync session not found.');
        else setError(null);
        setData(p);
      })
      .catch((e) => setError(e.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  });

  function onPick(ev) { open(ev?.detail?.sessionId); }
  function onKey(ev) { if (ev.key === 'Escape') close(); }
  onMount(() => {
    window.addEventListener('v2-sync-session-pick', onPick);
    window.addEventListener('keydown', onKey);
  });
  onCleanup(() => {
    window.removeEventListener('v2-sync-session-pick', onPick);
    window.removeEventListener('keydown', onKey);
  });

  return (
    <Show when={sessionId()}>
      <Portal>
        <div class="v2-modal__backdrop" onClick={close}>
          <div class="v2-modal" onClick={(e) => e.stopPropagation()}>
            <div class="v2-modal__actions">
              <button class="v2-btn-sm" onClick={close} title="Close (Esc)" aria-label="Close">✖</button>
            </div>

            <Show when={loading() && !data()}>
              <div class="v2-widget-skel">Loading session…</div>
            </Show>
            <Show when={error()}>
              <div class="v2-error">{error()}</div>
            </Show>

            <Show when={data()?.session}>
              {(() => {
                const s = data().session;
                const pb = data().pointsBreakdown || {};
                const at = data().attribution || {};
                return (
                  <>
                    <div class="v2-modal__head">
                      <div class="v2-modal__head-text">
                        <div class="v2-modal__name">
                          📜 Sync #{s.id} · {(s.syncType || 'mm').toUpperCase()}
                        </div>
                        <div class="v2-modal__sub">
                          {s.syncerName} · {fmtDate(s.startedAt)}
                          <Show when={s.finishedAt}> · {fmtDuration(s.startedAt, s.finishedAt)}</Show>
                        </div>
                      </div>
                    </div>

                    <div class="v2-stat-grid">
                      <div class="v2-stat v2-stat--gold">
                        <span class="v2-stat__label">Points awarded</span>
                        <span class="v2-stat__value">{qty(pb.total || 0)} pts</span>
                      </div>
                      <div class="v2-stat">
                        <span class="v2-stat__label">Files</span>
                        <span class="v2-stat__value">{qty(s.fileCount || 0)}</span>
                        <span class="v2-stat__sub">×{2} ea = {qty(pb.filePoints || 0)} pts</span>
                      </div>
                      <div class="v2-stat">
                        <span class="v2-stat__label">Items added</span>
                        <span class="v2-stat__value">{qty(s.itemsAdded || 0)}</span>
                        <span class="v2-stat__sub">×{5} ea = {qty(pb.itemPoints || 0)} pts</span>
                      </div>
                      <div class="v2-stat v2-stat--cyan">
                        <span class="v2-stat__label">Sales added</span>
                        <span class="v2-stat__value">{qty(s.salesAdded || 0)}</span>
                        <span class="v2-stat__sub">×{1} ea = {qty(pb.salePoints || 0)} pts</span>
                      </div>
                      <Show when={(s.ttcUpdated || 0) > 0}>
                        <div class="v2-stat">
                          <span class="v2-stat__label">TTC update</span>
                          <span class="v2-stat__value">✓</span>
                          <span class="v2-stat__sub">+{qty(pb.ttcPoints || 0)} pts</span>
                        </div>
                      </Show>
                      <Show when={(pb.syncBonus || 0) > 0}>
                        <div class="v2-stat">
                          <span class="v2-stat__label">Sync bonus</span>
                          <span class="v2-stat__value">+{qty(pb.syncBonus)} pts</span>
                        </div>
                      </Show>
                    </div>

                    <Show when={s.guilds?.length}>
                      <div class="v2-section-h">Guilds synced</div>
                      <div style={{ display: 'flex', gap: '6px', 'flex-wrap': 'wrap', 'margin-bottom': '8px' }}>
                        <For each={s.guilds}>{(g) => (
                          <span class="v2-tag">{typeof g === 'string' ? g : g?.name || g?.id || '—'}</span>
                        )}</For>
                      </div>
                    </Show>

                    <Show when={s.notes}>
                      <div class="v2-section-h">Notes</div>
                      <div style={{ 'font-size': '12px', color: 'var(--t-green-dim)', 'margin-bottom': '8px' }}>
                        {s.notes}
                      </div>
                    </Show>

                    <Show when={at.saleHistoryHidden}>
                      <div class="v2-section-h">Per-item attribution</div>
                      <div style={{ 'font-size': '12px', color: 'var(--t-green-dim)' }}>
                        Hidden by your privacy settings (hideSaleHistory).
                      </div>
                    </Show>

                    <Show when={!at.saleHistoryHidden && at.sales?.length}>
                      <div class="v2-section-h">
                        Per-item attribution
                        <span style={{ color: 'var(--t-green-dim)', 'font-weight': 'normal', 'margin-left': '6px' }}>
                          ({qty(at.attributedTotal)} sale{at.attributedTotal === 1 ? '' : 's'} bucketed by ingest window)
                        </span>
                      </div>
                      <div class="v2-modal__sales">
                        <For each={at.sales}>{(sale) => (
                          <div class="v2-sale-row">
                            <span class="v2-sale-row__time">{relTime(sale.timestamp)}</span>
                            <span class="v2-sale-row__main">
                              <Show when={sale.itemName}>
                                <span style={{ color: 'var(--t-green)' }}>{sale.itemName}</span>
                              </Show>
                              <Show when={sale.sellerName}>
                                <em> · </em>
                                <b class="v2-pick v2-pick--user" onClick={() => pickUser(sale.sellerName)}>{sale.sellerName}</b>
                              </Show>
                              <Show when={sale.quant > 1}><em> ×{sale.quant}</em></Show>
                              <Show when={sale.guildName}>
                                <em style={{ color: 'var(--t-green-dim)' }}> · {sale.guildName}</em>
                              </Show>
                            </span>
                            <span class="v2-sale-row__price">{gold(sale.price)}</span>
                          </div>
                        )}</For>
                      </div>
                    </Show>

                    <Show when={!at.saleHistoryHidden && !at.sales?.length}>
                      <div style={{ 'font-size': '12px', color: 'var(--t-green-dim)', 'margin-top': '8px' }}>
                        No per-item attribution available for this session window.
                      </div>
                    </Show>
                  </>
                );
              })()}
            </Show>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
